"use client";

import React from "react";
import { ArrowDown, BookOpen, Sparkles, TrendingUp } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function BlogHero() {
  const shouldReduceMotion = useReducedMotion();
  const handleScrollToContent = () => {
    const target = document.getElementById("articles-feed") || document.getElementById("blog-feed");
    if (target) {
      target.scrollIntoView({
        behavior: "smooth"
      });
    } else {
      window.scrollTo({
        top: window.innerHeight * 0.85,
        behavior: "smooth"
      });
    }
  };
  return <section id="bloghero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 right-1/4 h-96 w-96 rounded-full bg-secondary/40 blur-3xl" />
        <div className="absolute top-1/2 -left-20 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          <div className="lg:col-span-7">
            <motion.div initial={shouldReduceMotion ? {} : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }} className="flex flex-col items-start space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-3.5 py-1.5 shadow-2xs">
                <Sparkles className="size-3.5 text-accent" />
                <span className="font-mono text-xs font-semibold uppercase tracking-wider text-foreground">
                  SEO Insights &amp; Analysis
                </span>
              </div>

              <h1 className="font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                Modern SEO Insights &amp; Strategic Updates
              </h1>

              <p className="max-w-2xl text-base text-muted-foreground sm:text-lg lg:text-xl font-normal leading-relaxed">
                I share hands-on SEO research, Google algorithm breakdowns, and verified organic growth frameworks directly from my active client projects. No recycled theory—just practical execution strategies you can apply.
              </p>

              <div className="flex flex-wrap items-center gap-2 pt-2">
                <span className="text-xs font-medium text-muted-foreground mr-1">Focus Topics:</span>
                <Badge variant="outline" className="border-border bg-card/60 px-2.5 py-1 text-xs text-foreground font-medium">
                  Technical SEO
                </Badge>
                <Badge variant="outline" className="border-border bg-card/60 px-2.5 py-1 text-xs text-foreground font-medium">
                  AI Search &amp; GEO
                </Badge>
                <Badge variant="outline" className="border-border bg-card/60 px-2.5 py-1 text-xs text-foreground font-medium">
                  National &amp; B2B
                </Badge>
                <Badge variant="outline" className="border-border bg-card/60 px-2.5 py-1 text-xs text-foreground font-medium">
                  Ecommerce SEO
                </Badge>
              </div>

              <div className="flex flex-wrap items-center gap-4 pt-4">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium" asChild>
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                  </Link>
                </Button>

                <Button size="lg" variant="outline" className="border-border bg-background text-foreground hover:bg-secondary/50 font-medium" asChild>
                  <Link to="/CaseStudiesPage">
                    <TrendingUp className="size-4 mr-1 text-primary" />
                    Explore Case Studies
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? {} : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            ease: "easeOut",
            delay: 0.1
          }} className="relative mx-auto max-w-md lg:max-w-none">
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                <div className="aspect-[4/3] w-full overflow-hidden sm:aspect-[16/11]">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/HDR-Bracket-FaceNum-0-FocusArea-111110000.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/HDR-Bracket-FaceNum-0-FocusArea-111110000.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/HDR-Bracket-FaceNum-0-FocusArea-111110000.jpg-wvc-sizes" alt="Yassine Essaadi analyzing search data during an SEO consulting session" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-[transform] duration-500 hover:scale-105" />
                </div>

                <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-background/90 to-transparent" />

                <div className="absolute bottom-4 left-4 right-4 rounded-xl border border-border/80 bg-background/95 p-4 backdrop-blur-sm shadow-md">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground shadow-xs">
                      <BookOpen className="size-5" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-accent">
                        Expert Editorial
                      </p>
                      <p className="truncate text-sm font-semibold text-foreground">
                        Written by Yassine Essaadi
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Independent Freelance SEO Consultant
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pointer-events-none absolute -bottom-3 -right-3 -z-10 h-full w-full rounded-2xl border-2 border-accent/20" />
            </motion.div>
          </div>

        </div>

        <div className="mt-14 flex flex-col items-center justify-center text-center lg:mt-20">
          <Button variant="ghost" size="sm" onClick={handleScrollToContent} className="group flex flex-col items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-transparent" aria-label="Scroll to articles feed">
            <span>Explore All Articles</span>
            <motion.div animate={shouldReduceMotion ? {} : {
            y: [0, 4, 0]
          }} transition={{
            repeat: Infinity,
            duration: 1.8,
            ease: "easeInOut"
          }}>
              <ArrowDown className="size-4 text-primary transition-transform group-hover:translate-y-0.5" />
            </motion.div>
          </Button>
        </div>
      </div>
    </section>;
}